const DEFAULT_MAX_WIDTH = 28;
const COLUMN_GAP = 2;

type ColumnMeta = {
  width: number;
  isLabel: boolean;
  headingLines: string[];
};

export function formatRunsTable(tsv: string, maxWidth = DEFAULT_MAX_WIDTH): string {
  const lines = tsv.replace(/\r/g, "").split("\n");
  while (lines.length > 0 && lines[lines.length - 1].trim() === "") lines.pop();
  if (lines.length === 0) return "";

  const headings = lines[0].split("\t");
  const columnCount = headings.length;

  const dataRows: Array<string[] | null> = lines.slice(1).map((line) => {
    if (line.trim() === "") return null;
    const cells = line.split("\t");
    while (cells.length < columnCount) cells.push("");
    return cells;
  });

  const columns: ColumnMeta[] = headings.map((heading, index) => {
    const words = heading.trim().split(/\s+/).filter(Boolean);
    const longestWord = words.reduce((max, word) => Math.max(max, word.length), 0);
    let widestValue = 0;
    for (const row of dataRows) {
      if (row) widestValue = Math.max(widestValue, (row[index] ?? "").length);
    }
    const width = Math.max(widestValue, longestWord, 1);
    return { width, isLabel: index === 0, headingLines: wrapWords(words, width) };
  });

  const tables = splitIntoTables(columns, maxWidth);
  return tables.map((columnIndices) => renderTable(columnIndices, columns, dataRows)).join("\n\n");
}

/** Greedily pack whole words onto lines no wider than `width`. */
function wrapWords(words: string[], width: number): string[] {
  if (words.length === 0) return [""];
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    if (line === "") line = word;
    else if (line.length + 1 + word.length <= width) line += ` ${word}`;
    else {
      lines.push(line);
      line = word;
    }
  }
  lines.push(line);
  return lines;
}

function splitIntoTables(columns: ColumnMeta[], maxWidth: number): number[][] {
  const valueColumnIndices = columns.map((_, index) => index).slice(1);
  if (valueColumnIndices.length === 0) return [[0]];

  const widthOf = (indices: number[]) =>
    indices.reduce((sum, index) => sum + columns[index].width, 0) + COLUMN_GAP * (indices.length - 1);

  const tables: number[][] = [];
  let current: number[] = [];
  for (const valueColumn of valueColumnIndices) {
    if (current.length > 0 && widthOf([0, ...current, valueColumn]) > maxWidth) {
      tables.push([0, ...current]);
      current = [valueColumn];
    } else {
      current.push(valueColumn);
    }
  }
  tables.push([0, ...current]);
  return tables;
}

function renderTable(columnIndices: number[], columns: ColumnMeta[], dataRows: Array<string[] | null>): string {
  const headerHeight = Math.max(...columnIndices.map((index) => columns[index].headingLines.length));
  const out: string[] = [];

  for (let lineIndex = 0; lineIndex < headerHeight; lineIndex++) {
    const cells = columnIndices.map((index) => {
      const { headingLines, width, isLabel } = columns[index];
      const topPadding = headerHeight - headingLines.length;
      const word = lineIndex >= topPadding ? headingLines[lineIndex - topPadding] : "";
      return pad(word, width, isLabel);
    });
    out.push(joinCells(cells));
  }

  for (const row of dataRows) {
    if (!row) {
      out.push("");
      continue;
    }
    out.push(
      joinCells(columnIndices.map((index) => pad(row[index] ?? "", columns[index].width, columns[index].isLabel))),
    );
  }

  return out.join("\n");
}

const pad = (text: string, width: number, isLabel: boolean): string =>
  isLabel ? text.padEnd(width) : text.padStart(width);

const joinCells = (cells: string[]): string => cells.join(" ".repeat(COLUMN_GAP)).replace(/\s+$/, "");
